import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CircleIcon from '@mui/icons-material/Circle';
import { Button } from '~common/components';
import useStyles from './styles';

dayjs.extend(AdvancedFormat);

const ContentComponent = ({
  appointmentData, userType,
}) => {
  const {
    startDate,
    endDate,
    subject,
    title,
    color,
    tutor,
    learner,
    callDetails,
  } = appointmentData;
  const { classes } = useStyles({ color });

  const getMeetingLinkInfo = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/;
    const match = text.match(urlRegex);
    if (!match) return null;

    const url = match[0];
    let platform = 'Meeting';
    let iconUrl = null;
    let iconHeight = 20;

    const platforms = [
      {
        name: 'Google Meet', regex: /meet\.google\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg', iconHeight: 20,
      },
      {
        name: 'Zoom', regex: /zoom\.us/, iconUrl: 'https://www.logo.wine/a/logo/Zoom_Video_Communications/Zoom_Video_Communications-Logo.wine.svg', iconHeight: 30,
      },
      {
        name: 'Microsoft Teams', regex: /teams\.microsoft\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg', iconHeight: 20,
      },
      {
        name: 'Skype', regex: /join\.skype\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/6/60/Skype_logo_%282019%E2%80%93present%29.svg', iconHeight: 20,
      },
      {
        name: 'Webex', regex: /webex\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0d/Cisco_Webex_logo.svg', iconHeight: 20,
      },
      {
        name: 'Discord', regex: /discord\.gg/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Discord_Color_Icon.svg', iconHeight: 22,
      },
      {
        name: 'Jitsi Meet', regex: /jitsi\.org|meet\.jit\.si/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5e/Jitsi_logo.svg', iconHeight: 20,
      },
      {
        name: 'GoToMeeting', regex: /gotomeeting\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/e/e8/GoToMeeting_logo.svg', iconHeight: 20,
      },
    ];

    const detectedPlatform = platforms.find((p) => p.regex.test(url));
    if (detectedPlatform) {
      platform = detectedPlatform.name;
      iconUrl = detectedPlatform.iconUrl;
      iconHeight = detectedPlatform.iconHeight;
    }

    return {
      url, platform, iconUrl, iconHeight,
    };
  };

  const renderCallDetailsWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.filter(Boolean).map((part, index) => {
      if (part.startsWith('http')) {
        return (
          <a href={part} key={index} target="_blank" rel="noreferrer">
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const meetingInfo = getMeetingLinkInfo(callDetails);
  const joinButtonText = meetingInfo ? 'Join Session' : '';

  const now = dayjs();
  const meetingStart = dayjs(startDate);
  const showButtonStartTime = meetingStart.subtract(30, 'minutes');
  const showButtonEndTime = meetingStart.add(1, 'hour');
  const shouldShowButton = (now.isAfter(showButtonStartTime) && now.isBefore(showButtonEndTime))
    || userType === 'tutor';

  // Show more / less logic
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(window.getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * 3;
      setIsOverflowing(textRef.current.scrollHeight > maxHeight + 1);
    }
  }, [callDetails]);

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} className={classes.nameRow}>
        <CircleIcon className={classes.dot} />
        <Typography className={classes.fullName} variant="body2" component="div">
          {userType === 'tutor' ? learner.fullName : tutor.fullName}
        </Typography>
      </Grid>

      {userType === 'parent' && (
        <Grid item xs={12} className={classes.nameRow}>
          <Typography className={classes.fullName} variant="body2" component="div">
            {learner.fullName}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography className={classes.subject} variant="subtitle1" component="div">
          {subject.subjectName}
        </Typography>
        <Typography className={classes.title} variant="subtitle1" component="div">
          {title}
        </Typography>

        {callDetails && (
          <div style={{ marginTop: '8px' }}>
            <Typography
              ref={textRef}
              variant="body2"
              className={classes.link}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: expanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {renderCallDetailsWithLinks(callDetails)}
            </Typography>

            {isOverflowing && (
              <Typography
                variant="body2"
                style={{
                  color: '#1976d2',
                  cursor: 'pointer',
                  marginTop: '4px',
                  marginBottom: '12px',
                  display: 'block',
                  fontWeight: 500,
                }}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </Typography>
            )}

            {meetingInfo && shouldShowButton && (
              <Button
                variant="outlined"
                href={meetingInfo.url}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: '0px' }}
                onClick={(e) => e.stopPropagation()}
                endIcon={
                  meetingInfo.iconUrl && (
                    <img
                      src={meetingInfo.iconUrl}
                      alt={`${meetingInfo.platform} logo`}
                      height={meetingInfo.iconHeight}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )
                }
              >
                {joinButtonText}
              </Button>
            )}
          </div>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography style={{ marginTop: '10px' }} className={classes.date} variant="subtitle1" component="div">
          {dayjs(startDate).format('dddd, Do MMMM')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography className={classes.time} variant="subtitle1" component="div">
          {dayjs(startDate).format('hh:mm A')} - {dayjs(endDate).format('hh:mm A')}
        </Typography>
      </Grid>
    </Grid>
  );
};

ContentComponent.propTypes = {
  appointmentData: PropTypes.shape({
    title: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    id: PropTypes.string,
    subject: PropTypes.shape({
      subjectName: PropTypes.string.isRequired,
    }),
    learner: PropTypes.shape({
      fullName: PropTypes.string.isRequired,
    }),
    tutor: PropTypes.shape({
      fullName: PropTypes.string.isRequired,
    }),
    allDay: PropTypes.bool,
    callDetails: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string.isRequired,
  }).isRequired,
  userType: PropTypes.oneOf(['admin', 'tutor', 'parent', 'learner']).isRequired,
};

export default ContentComponent;
